import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Paper, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  fetchSegments, 
  fetchDocuments,
  fetchProcessedContent
} from '../utils/api';
import { processLatexString } from '../components/LatexRenderer';

const Segments = () => {
  const { documentId } = useParams();
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [processedContent, setProcessedContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const navigate = useNavigate();
  
  // Load segments
  useEffect(() => {
    const loadSegments = async () => {
      try {
        setLoading(true);
        
        // Load document name if documentId is provided
        if (documentId) {
          try {
            const docResponse = await fetchDocuments();
            const document = docResponse.data.find(doc => doc.id === parseInt(documentId));
            if (document) {
              setDocumentName(document.name);
            }
          } catch (err) {
            console.error('Failed to load document name:', err);
          }
        }
        
        // Load segments
        const response = await fetchSegments(documentId || null);
        setSegments(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to load segments:', err);
        setError('Failed to load segments. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadSegments();
  }, [documentId]);
  
  const handleViewProcessed = async (segmentId) => {
    try {
      setContentLoading(true);
      const response = await fetchProcessedContent(segmentId);
      setProcessedContent(response.data);
      
      // Find segment details
      const segment = segments.find(s => s.id === segmentId);
      setSelectedSegment(segment);
    } catch (err) {
      console.error('Failed to load processed content:', err);
      // If 404, show empty processed content
      if (err.response && err.response.status === 404) {
        setProcessedContent(null);
        
        // Find segment details
        const segment = segments.find(s => s.id === segmentId);
        setSelectedSegment(segment);
      }
    } finally {
      setContentLoading(false);
    }
  };
  
  const handleCloseDialog = () => {
    setSelectedSegment(null);
    setProcessedContent(null);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <div>
      <Typography variant="h4" gutterBottom>
        {documentId 
          ? `Content Segments: ${documentName || `Document #${documentId}`}` 
          : 'All Content Segments'
        }
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {segments.length === 0 && !loading && !error ? (
        <Alert severity="info">
          No segments found. {documentId ? 'This document might not be segmented yet.' : 'Process some documents to create segments.'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                {!documentId && <TableCell>Document ID</TableCell>}
                <TableCell>Question</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {segments.map((segment) => (
                <TableRow key={segment.id}>
                  <TableCell>{segment.id}</TableCell>
                  {!documentId && (
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => navigate(`/segments/${segment.source_document_id}`)}
                      >
                        {segment.source_document_id}
                      </Button>
                    </TableCell>
                  )}
                  <TableCell>
                    {`${segment.question_number}${segment.part_letter ? `${segment.part_letter}` : ''}`}
                  </TableCell>
                  <TableCell>{segment.question_type}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {segment.is_categorized && (
                        <Chip label="Categorized" color="success" size="small" />
                      )}
                      {segment.is_difficulty_rated && (
                        <Chip label="Rated" color="success" size="small" />
                      )}
                      {segment.is_answer_verified && (
                        <Chip label="Solution" color="success" size="small" />
                      )}
                      {segment.is_latex_formatted && (
                        <Chip label="LaTeX" color="success" size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => handleViewProcessed(segment.id)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Segment details dialog */}
      <Dialog 
        open={!!selectedSegment} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedSegment && (
          <>
            <DialogTitle>
              Segment #{selectedSegment.id}: Question {selectedSegment.question_number}
              {selectedSegment.part_letter && ` Part ${selectedSegment.part_letter}`}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Content
                      </Typography>
                      <Box sx={{ whiteSpace: 'pre-wrap' }}>
                        {processLatexString(selectedSegment.content)}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Processed Content
                  </Typography>
                  
                  {contentLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                      <CircularProgress />
                    </Box>
                  ) : processedContent ? (
                    <Grid container spacing={2}>
                      {processedContent.topic_path && (
                        <Grid item xs={12} md={6}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="subtitle1" color="textSecondary">
                                Topic Path
                              </Typography>
                              <Typography variant="body1">
                                {processedContent.topic_path}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}
                      
                      {processedContent.difficulty_rating && (
                        <Grid item xs={12} md={6}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="subtitle1" color="textSecondary">
                                Difficulty Rating
                              </Typography>
                              <Typography variant="body1">
                                {processedContent.difficulty_rating}/5
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}
                      
                      {processedContent.answer_text && (
                        <Grid item xs={12}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="subtitle1" color="textSecondary">
                                Answer
                              </Typography>
                              <Box sx={{ whiteSpace: 'pre-wrap' }}>
                                {processLatexString(processedContent.answer_text)}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Alert severity="info">
                      No processed content available for this segment.
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default Segments; 
